import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

export const DesiresRow = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(-1);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftText, setDraftText] = useState("");

  useEffect(() => {
    api.get("/desires").then((r) => setItems(r.data.items)).catch((err) => console.error("Desires load failed:", err));
  }, []);

  const save = async (i) => {
    const next = items.map((it, idx) => (idx === i ? { title: draftTitle, text: draftText } : it));
    try {
      await api.put("/desires", { items: next });
      setItems(next);
      setEditing(-1);
      toast.success("Vision saved");
    } catch { toast.error("Couldn't save vision"); }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 pt-10 pb-16" data-testid="desires-section">
      <p className="text-[10px] tracking-[0.5em] uppercase text-[#00E5FF] mb-4" data-testid="desires-eyebrow">Vision & Desires</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((item, i) => (
          <motion.div
            key={`desire-slot-${i}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }}
            className="border border-white/10 rounded-md bg-[#0F0F11] p-5 min-h-36 flex flex-col"
            data-testid={`desire-box-${i}`}
          >
            <div className="flex items-center justify-between mb-3 gap-3">
              {editing === i ? (
                <Input
                  value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} placeholder="Title"
                  className="bg-white/5 border-white/10 text-white rounded-sm h-7 text-xs uppercase tracking-[0.2em] focus-visible:ring-[#00E5FF]"
                  data-testid={`desire-title-input-${i}`}
                />
              ) : (
                <span className="font-display text-sm uppercase tracking-[0.25em] text-[#D4AF37]" data-testid={`desire-title-${i}`}>
                  {item.title || "Untitled"}
                </span>
              )}
              {editing === i ? (
                <button onClick={() => save(i)} className="shrink-0 text-emerald-400 hover:text-emerald-300 transition-colors duration-200" data-testid={`desire-save-${i}`}>
                  <Check className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={() => { setEditing(i); setDraftTitle(item.title); setDraftText(item.text); }} className="shrink-0 text-zinc-600 hover:text-[#00E5FF] transition-colors duration-200" data-testid={`desire-edit-${i}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {editing === i ? (
              <Textarea
                value={draftText} onChange={(e) => setDraftText(e.target.value)} rows={4} autoFocus
                className="bg-white/5 border-white/10 text-white rounded-sm text-sm focus-visible:ring-[#00E5FF]"
                data-testid={`desire-input-${i}`}
              />
            ) : (
              <p className={`text-sm leading-relaxed ${item.text ? "text-zinc-200" : "text-zinc-600 italic"}`} data-testid={`desire-text-${i}`}>
                {item.text || "Write the vision…"}
              </p>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
};
